import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { readStored, writeStored } from '../storage';
import type { MessageKey } from './en';
import { isLanguage, translate, type Language, type MessageVars } from './translate';

const LANG_KEY = 'lumina.lang.v1';

export type TFunction = (key: MessageKey, vars?: MessageVars) => string;

interface I18nContextValue {
  lang: Language;
  setLang: (lang: Language) => void;
  t: TFunction;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function initialLanguage(): Language {
  const stored = readStored<unknown>(LANG_KEY, null);
  return isLanguage(stored) ? stored : 'en';
}

export function I18nProvider({ children, initial }: { children: ReactNode; initial?: Language }) {
  const [lang, setLangState] = useState<Language>(() => initial ?? initialLanguage());

  const setLang = useCallback((next: Language) => {
    setLangState(next);
    writeStored(LANG_KEY, next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback<TFunction>((key, vars) => translate(lang, key, vars), [lang]);
  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** `const { t } = useI18n();` then `t('common.save')`. Works without a provider (English). */
export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (context) {
    return context;
  }
  return { lang: 'en', setLang: () => undefined, t: (key, vars) => translate('en', key, vars) };
}
